/**
 * tdesign v1.5.1
 * (c) 2023 tdesign
 * @license MIT
 */

import { Button } from '../button/index.mjs';
import { Icon } from '../icon/index.mjs';
import { Link } from '../link/index.mjs';
import { Row, Col } from '../grid/index.mjs';
import { Aside, Layout, Header, Footer, Content } from '../layout/index.mjs';
import { Divider } from '../divider/index.mjs';
import { Space } from '../space/index.mjs';
import { Affix } from '../affix/index.mjs';
import { Anchor, AnchorItem, AnchorTarget } from '../anchor/index.mjs';
import { Breadcrumb, BreadcrumbItem } from '../breadcrumb/index.mjs';
import { Dropdown, DropdownItem, DropdownMenu } from '../dropdown/index.mjs';
import { Menu, HeadMenu, Submenu, MenuItem, MenuGroup } from '../menu/index.mjs';
import { Pagination, PaginationMini } from '../pagination/index.mjs';
import { Steps, StepItem } from '../steps/index.mjs';
import { Tabs, TabPanel } from '../tabs/index.mjs';
import { StickyItem, StickyTool } from '../sticky-tool/index.mjs';
import { BackTop } from '../back-top/index.mjs';
import { AutoComplete, HighlightOption } from '../auto-complete/index.mjs';
import { Cascader, CascaderPanel } from '../cascader/index.mjs';
import { Checkbox, CheckboxGroup } from '../checkbox/index.mjs';
import { DatePicker, DatePickerPanel, DateRangePicker, DateRangePickerPanel } from '../date-picker/index.mjs';
import { Form, FormItem } from '../form/index.mjs';
import { InputAdornment } from '../input-adornment/index.mjs';
import { Input, InputGroup } from '../input/index.mjs';
import { InputNumber } from '../input-number/index.mjs';
import { Radio, RadioGroup, RadioButton } from '../radio/index.mjs';
import { Select, Option, OptionGroup } from '../select/index.mjs';
import { Slider, SliderButton } from '../slider/index.mjs';
import { Switch } from '../switch/index.mjs';
import { TagInput } from '../tag-input/index.mjs';
import { SelectInput } from '../select-input/index.mjs';
import { Textarea } from '../textarea/index.mjs';
import { Transfer } from '../transfer/index.mjs';
import { TimePicker, TimeRangePicker, TimePickerPanel } from '../time-picker/index.mjs';
import { TreeSelect } from '../tree-select/index.mjs';
import { ColorPickerPanel, ColorPicker } from '../color-picker/index.mjs';
import { RangeInput, RangeInputPopup } from '../range-input/index.mjs';
import { Avatar, AvatarGroup } from '../avatar/index.mjs';
import { Badge } from '../badge/index.mjs';
import { Calendar } from '../calendar/index.mjs';
import { Card } from '../card/index.mjs';
import { Comment } from '../comment/index.mjs';
import { Image } from '../image/index.mjs';
import { ImageViewer } from '../image-viewer/index.mjs';
import { List, ListItem, ListItemMeta } from '../list/index.mjs';
import { Progress } from '../progress/index.mjs';
import { Skeleton } from '../skeleton/index.mjs';
import { Swiper, SwiperItem } from '../swiper/index.mjs';
import { BaseTable, PrimaryTable, EnhancedTable, Table } from '../table/index.mjs';
import { Tag, CheckTag } from '../tag/index.mjs';
import { Timeline, TimelineItem } from '../timeline/index.mjs';
import { Tooltip } from '../tooltip/index.mjs';
import { Tree } from '../tree/index.mjs';
import { Collapse, CollapsePanel } from '../collapse/index.mjs';
import { Watermark } from '../watermark/index.mjs';
import { Rate } from '../rate/index.mjs';
import { Alert } from '../alert/index.mjs';
import { Dialog } from '../dialog/index.mjs';
import { Drawer } from '../drawer/index.mjs';
import { Guide } from '../guide/index.mjs';
import { Loading } from '../loading/index.mjs';
import { Message } from '../message/index.mjs';
import { Notification } from '../notification/index.mjs';
import { Popconfirm } from '../popconfirm/index.mjs';
import { Popup } from '../popup/index.mjs';
import { Upload } from '../upload/index.mjs';
import { ConfigProvider } from '../config-provider/index.mjs';
import { DialogPlugin } from '../dialog/plugin.mjs';
import LoadingPlugin from '../loading/plugin.mjs';
import MessagePlugin from '../message/plugin.mjs';
import NotificationPlugin from '../notification/plugin.mjs';

var components = /*#__PURE__*/Object.freeze({
	__proto__: null,
	Button: Button,
	Icon: Icon,
	Link: Link,
	Row: Row,
	Col: Col,
	Aside: Aside,
	Layout: Layout,
	Header: Header,
	Footer: Footer,
	Content: Content,
	Divider: Divider,
	Space: Space,
	Affix: Affix,
	Anchor: Anchor,
	AnchorItem: AnchorItem,
	AnchorTarget: AnchorTarget,
	Breadcrumb: Breadcrumb,
	BreadcrumbItem: BreadcrumbItem,
	Dropdown: Dropdown,
	DropdownItem: DropdownItem,
	DropdownMenu: DropdownMenu,
	Menu: Menu,
	HeadMenu: HeadMenu,
	Submenu: Submenu,
	MenuItem: MenuItem,
	MenuGroup: MenuGroup,
	Pagination: Pagination,
	PaginationMini: PaginationMini,
	Steps: Steps,
	StepItem: StepItem,
	Tabs: Tabs,
	TabPanel: TabPanel,
	StickyItem: StickyItem,
	StickyTool: StickyTool,
	BackTop: BackTop,
	AutoComplete: AutoComplete,
	HighlightOption: HighlightOption,
	Cascader: Cascader,
	CascaderPanel: CascaderPanel,
	Checkbox: Checkbox,
	CheckboxGroup: CheckboxGroup,
	DatePicker: DatePicker,
	DatePickerPanel: DatePickerPanel,
	DateRangePicker: DateRangePicker,
	DateRangePickerPanel: DateRangePickerPanel,
	Form: Form,
	FormItem: FormItem,
	InputAdornment: InputAdornment,
	Input: Input,
	InputGroup: InputGroup,
	InputNumber: InputNumber,
	Radio: Radio,
	RadioGroup: RadioGroup,
	RadioButton: RadioButton,
	Select: Select,
	Option: Option,
	OptionGroup: OptionGroup,
	Slider: Slider,
	SliderButton: SliderButton,
	Switch: Switch,
	TagInput: TagInput,
	SelectInput: SelectInput,
	Textarea: Textarea,
	Transfer: Transfer,
	TimePicker: TimePicker,
	TimeRangePicker: TimeRangePicker,
	TimePickerPanel: TimePickerPanel,
	TreeSelect: TreeSelect,
	ColorPickerPanel: ColorPickerPanel,
	ColorPicker: ColorPicker,
	RangeInput: RangeInput,
	RangeInputPopup: RangeInputPopup,
	Avatar: Avatar,
	AvatarGroup: AvatarGroup,
	Badge: Badge,
	Calendar: Calendar,
	Card: Card,
	Comment: Comment,
	Image: Image,
	ImageViewer: ImageViewer,
	List: List,
	ListItem: ListItem,
	ListItemMeta: ListItemMeta,
	Progress: Progress,
	Skeleton: Skeleton,
	Swiper: Swiper,
	SwiperItem: SwiperItem,
	BaseTable: BaseTable,
	PrimaryTable: PrimaryTable,
	EnhancedTable: EnhancedTable,
	Table: Table,
	Tag: Tag,
	CheckTag: CheckTag,
	Timeline: Timeline,
	TimelineItem: TimelineItem,
	Tooltip: Tooltip,
	Tree: Tree,
	Collapse: Collapse,
	CollapsePanel: CollapsePanel,
	Watermark: Watermark,
	Rate: Rate,
	Alert: Alert,
	DialogPlugin: DialogPlugin,
	Dialog: Dialog,
	Drawer: Drawer,
	Guide: Guide,
	LoadingPlugin: LoadingPlugin,
	Loading: Loading,
	MessagePlugin: MessagePlugin,
	Message: Message,
	NotifyPlugin: NotificationPlugin,
	Notification: Notification,
	Popconfirm: Popconfirm,
	Popup: Popup,
	Upload: Upload,
	ConfigProvider: ConfigProvider
});

export { components as c };
//# sourceMappingURL=dep-d2b3438a.mjs.map
